import { Activity } from 'lucide-react';
import { clsx } from 'clsx';
import { Badge, Button, Callout, useToast } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { StediDiagnostics } from '../api/verificationApi';
import { useStediDiagnostics } from '../hooks/useConnections';
import { getErrorMessage } from '../utils/format';
import styles from './StediDiagnosticsCard.module.css';

/**
 * Runs every dental mock in the catalog against Stedi and shows exactly what
 * came back — key, network, request shape and mock values checked in one go.
 */
export function StediDiagnosticsCard() {
  const toast = useToast();
  const diag = useStediDiagnostics();
  const data: StediDiagnostics | undefined = diag.data;

  function run() {
    diag.mutate(undefined, {
      onError: (err) => toast.error(getErrorMessage(err, 'Diagnostics could not run')),
    });
  }

  const passed = data ? data.results.filter((r) => r.ok).length : 0;
  const allOk = !!data && passed === data.results.length;

  return (
    <section className={clsx(patterns.panel, styles.card)}>
      <div className={styles.head}>
        <div>
          <div className={styles.title}>Stedi diagnostics</div>
          <div className={styles.desc}>
            Sends each of Stedi&apos;s dental mock requests (Cigna, MetLife, Ameritas, Anthem BCBS of
            CA, UnitedHealthcare) plus two error mocks, and shows what the payer answered. No patient
            data is involved. Use this before re-running failed verifications.
          </div>
        </div>
        <Button size="sm" leftIcon={<Activity size={14} aria-hidden />} onClick={run} isLoading={diag.isPending}>
          Run mock checks
        </Button>
      </div>

      {data && (
        <>
          <Callout variant={allOk ? 'success' : 'warning'} title={allOk ? 'Stedi is working' : `${passed} of ${data.results.length} passed`}>
            {data.verdict}
            <span className={styles.metaLine}>
              Transport: {data.config.transport === 'fixture' ? 'recorded responses (no network)' : 'live Stedi test mode'} · Organization
              sent: {data.organizationName}
            </span>
          </Callout>

          <div className={patterns.gridTable}>
            <div className={clsx(patterns.gridTableHeader, styles.cols)}>
              <span>Payer</span>
              <span>Mock member</span>
              <span>Result</span>
              <span>What Stedi / the payer said</span>
              <span>Trace</span>
            </div>
            {data.results.map((r) => (
              <div key={r.key} className={clsx(patterns.gridTableRow, styles.cols)}>
                <div>
                  <div className={patterns.cellPrimary}>{r.payerName}</div>
                  <div className={patterns.cellSmMuted}>payer {r.payerId}</div>
                </div>
                <div className={styles.mono}>{r.memberId}</div>
                <div>
                  {r.ok ? (
                    <Badge variant="success">{r.expected === 'error' ? 'Expected error' : 'Benefits returned'}</Badge>
                  ) : (
                    <Badge variant="error">{r.error?.kind.replace(/_/g, ' ') ?? 'failed'}</Badge>
                  )}
                  <div className={patterns.cellSmMuted}>{r.durationMs} ms</div>
                </div>
                <div className={styles.said}>
                  {r.ok && r.summary && <span>{r.summary}</span>}
                  {r.error && (
                    <>
                      <div>{r.error.aaa ?? r.error.message}</div>
                      {r.error.technical && r.error.technical !== r.error.aaa && (
                        <div className={clsx(styles.mono, patterns.cellSmMuted)}>{r.error.technical}</div>
                      )}
                    </>
                  )}
                </div>
                <div className={clsx(styles.mono, patterns.cellSmMuted)}>{r.externalId ?? '—'}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
